import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRightIcon,
  ChatBubbleLeftRightIcon,
  CheckIcon,
  HeartIcon,
  QrCodeIcon,
  ShieldCheckIcon,
  ShoppingBagIcon,
  TruckIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';
import { RingLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import { fetchWithAuth } from '../services/api';
import { trackVisit } from '../services/visitTrackingService';
import SEO from '../components/SEO';
import Footer from '../components/Footer';
import { formatPrice } from '../utils/currency';
import { hasValidAuthentication, saveIntendedDestination } from '../services/auth';
import './ProductsPage.css';

const ProductsPage = () => {
  const { t } = useTranslation();
  const { lang } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchWithAuth('/products', { isPublic: true });
      setProducts(data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError(err.message || t('productsPage.errorFetching'));
      toast.error(t('productsPage.errorFetchingToast'));
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    trackVisit('PRODUCT', 'catalogue');
    fetchProducts();
  }, [fetchProducts]);

  const handleBuyNow = (productId) => {
    const destination = `/${lang}/checkout/${productId}`;

    if (hasValidAuthentication()) {
      navigate(destination);
      return;
    }

    saveIntendedDestination(destination);
    navigate(`/${lang}/login`);
  };

  const resolveImageUrl = (imageUrl) => {
    if (!imageUrl) return '';
    if (imageUrl.startsWith('http') || imageUrl.startsWith('/')) return imageUrl;
    return `/${imageUrl}`;
  };

  const benefits = [
    {
      icon: ShieldCheckIcon,
      title: t('productsPage.feature1Title'),
      description: t('productsPage.feature1Description')
    },
    {
      icon: QrCodeIcon,
      title: t('productsPage.feature2Title'),
      description: t('productsPage.feature2Description')
    },
    {
      icon: HeartIcon,
      title: t('productsPage.feature3Title'),
      description: t('productsPage.feature3Description')
    }
  ];

  const includedItems = [
    { icon: WrenchScrewdriverIcon, text: t('productsPage.includedAdhesive') },
    { icon: TruckIcon, text: t('productsPage.includedDelivery') },
    { icon: ChatBubbleLeftRightIcon, text: t('productsPage.includedSupport') }
  ];

  if (loading) {
    return (
      <div className="products-state" data-testid="products-page-loading">
        <RingLoader color="#a76f43" size={64} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="products-state">
        <div className="products-error" role="alert">
          <p>{error}</p>
          <button type="button" onClick={fetchProducts}>{t('productsPage.retry')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="products-page" data-testid="products-page">
      <SEO page="products" />

      <header className="products-intro">
        <div className="products-shell">
          <p className="products-kicker">{t('productsPage.pageEyebrow')}</p>
          <h1 data-testid="products-page-title">{t('productsPage.title')}</h1>
          <p className="products-intro-copy">{t('productsPage.subtitle')}</p>
        </div>
      </header>

      <main>
        <section className="products-catalog" aria-labelledby="products-catalog-title">
          <h2 id="products-catalog-title" className="sr-only">{t('productsPage.title')}</h2>
          <div className="products-shell">
            {products.length === 0 ? (
              <div className="products-empty" data-testid="products-page-no-products">
                <QrCodeIcon aria-hidden="true" />
                <h3>{t('productsPage.noProductsFound')}</h3>
                <p>{t('productsPage.noProductsMessage')}</p>
              </div>
            ) : (
              <div
                className={`product-list ${products.length > 1 ? 'product-list-multiple' : ''}`}
                data-testid="products-grid"
              >
                {products.map((product) => (
                  <article
                    key={product.id}
                    className="product-showcase"
                    data-testid={`product-card-${product.id}`}
                  >
                    <div className="product-visual">
                      {product.comingSoon && (
                        <div className="product-badge">
                          {t('productsPage.comingSoonBadge')}
                        </div>
                      )}
                      <img
                        src={resolveImageUrl(product.imageUrl)}
                        alt={product.name || t('productsPage.productName')}
                        data-testid={`product-image-${product.id}`}
                      />
                    </div>

                    <div className="product-details">
                      <p className="product-details-kicker">{t('productsPage.productKicker')}</p>
                      <h2 data-testid={`product-name-${product.id}`}>
                        {t('productsPage.productName')}
                      </h2>
                      <p
                        className="product-summary"
                        data-testid={`product-description-${product.id}`}
                      >
                        {t('productsPage.productSummary')}
                      </p>

                      <div className="product-benefit-list">
                        {benefits.map(({ icon: Icon, title }) => (
                          <span key={title}>
                            <CheckIcon aria-hidden="true" />
                            {title}
                          </span>
                        ))}
                      </div>

                      <div className="product-purchase-row">
                        <div>
                          <p
                            className="product-price-v2"
                            data-testid={`product-price-${product.id}`}
                          >
                            {formatPrice(product.price, lang)}
                          </p>
                          <p className="product-payment-note">
                            {t('productsPage.oneTimePayment')}
                          </p>
                        </div>

                        <button
                          type="button"
                          disabled={product.comingSoon}
                          onClick={() => handleBuyNow(product.id)}
                          className="product-buy-button-v2"
                          data-testid={`product-buy-button-${product.id}`}
                        >
                          <ShoppingBagIcon aria-hidden="true" />
                          <span>
                            {product.comingSoon
                              ? t('productsPage.comingSoonButton')
                              : t('productsPage.buyNowButton')}
                          </span>
                          {!product.comingSoon && <ArrowRightIcon aria-hidden="true" />}
                        </button>
                      </div>

                      <p className="product-order-note">
                        <ShieldCheckIcon aria-hidden="true" />
                        {t('productsPage.orderNote')}
                      </p>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>

        {products.length > 0 && (
          <section className="product-included">
            <div className="products-shell product-included-inner">
              <div className="product-included-heading">
                <p>{t('productsPage.includedEyebrow')}</p>
                <h2>{t('productsPage.includedTitle')}</h2>
              </div>
              <div className="product-included-list">
                {includedItems.map(({ icon: Icon, text }) => (
                  <div key={text}>
                    <span><Icon aria-hidden="true" /></span>
                    <p>{text}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="product-story">
          <div className="products-shell">
            <div className="product-story-heading">
              <h2>{t('productsPage.detailsTitle')}</h2>
              <span>{t('productsPage.detailsSubtitle')}</span>
            </div>

            <div className="product-story-grid">
              {benefits.map(({ icon: Icon, title, description }, index) => (
                <article key={title}>
                  <div className="product-story-number">0{index + 1}</div>
                  <span className="product-story-icon"><Icon aria-hidden="true" /></span>
                  <h3>{title}</h3>
                  <p>{description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ProductsPage;
